/**
 * Stale-bundle (chunk load) error detection and recovery.
 *
 * Every build gives JS chunks new content-hashed filenames. A tab that was
 * loaded before a deploy still holds the old chunk names, so the next
 * client-side navigation (a `<Link>` transition, a lazy `import()`) requests a
 * file that no longer exists on the server. The bundler runtime then throws
 * something like:
 *
 *   Failed to load chunk /_next/static/chunks/866b4cdd650b1f70.js from module 8875
 *
 * That is not a real application failure - the user just needs the newer HTML.
 * A single reload fixes it, so we detect these errors and reload once instead
 * of showing an error screen.
 */

const CHUNK_ERROR_PATTERNS = [
  /failed to load chunk/i, // Turbopack
  /loading chunk \S+ failed/i, // webpack
  /loading css chunk/i, // webpack CSS
  /failed to fetch dynamically imported module/i, // Chrome
  /error loading dynamically imported module/i, // Firefox
  /importing a module script failed/i, // Safari
]

const RELOAD_MARKER_KEY = "planami:chunk-reload-at"

/** Don't reload again if we already tried within this window (loop guard). */
const RELOAD_COOLDOWN_MS = 30_000

function matchesChunkError(value: unknown, depth: number): boolean {
  if (!value || depth > 3) return false

  if (typeof value === "string") {
    return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(value))
  }

  if (typeof value !== "object") return false

  const error = value as { name?: unknown; message?: unknown; cause?: unknown }

  if (error.name === "ChunkLoadError") return true
  if (typeof error.message === "string" && matchesChunkError(error.message, depth + 1)) return true

  // Turbopack wraps the underlying fetch/script failure in `cause`.
  return matchesChunkError(error.cause, depth + 1)
}

/**
 * True when the error looks like a missing/failed JS or CSS chunk rather than
 * an application bug.
 */
export function isChunkLoadError(error: unknown): boolean {
  return matchesChunkError(error, 0)
}

/**
 * Reload the page once to pick up the current build.
 *
 * Returns false when a reload was already attempted recently, so callers can
 * fall back to a normal error UI instead of looping.
 */
export function recoverFromChunkLoadError(): boolean {
  if (typeof window === "undefined") return false

  try {
    const lastAttempt = Number(window.sessionStorage.getItem(RELOAD_MARKER_KEY) ?? 0)
    if (lastAttempt && Date.now() - lastAttempt < RELOAD_COOLDOWN_MS) {
      return false
    }
    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()))
  } catch {
    // sessionStorage can be unavailable (private mode, blocked cookies).
    // Without a loop guard it is safer not to reload automatically.
    return false
  }

  window.location.reload()
  return true
}
